import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import { Channel, channelMark, channelName, CREATE_REACH_POST, REACH_CHANNELS } from './reach';

/**
 * Reach → Calendar → "Write a post": a person posts by hand. One post per
 * channel picked, through the same post service every other producer uses;
 * the person is their own approver, so it goes straight on the calendar.
 */

type When = 'now' | 'later' | 'draft';

const localInputValue = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function WritePost({ onClose, onPosted }: { onClose: () => void; onPosted: () => void }) {
  const { accountId } = useAccount();
  const { data } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId,
  });
  const channels = useMemo(() => (data?.socialProfiles || []).filter((c) => c.isActive), [data]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [image, setImage] = useState('');
  const [when, setWhen] = useState<When>('now');
  const [at, setAt] = useState(() => localInputValue(new Date(Date.now() + 60 * 60 * 1000)));
  const [busy, setBusy] = useState(false);
  const [createPost] = useMutation(CREATE_REACH_POST);

  const toggle = (id: string) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const mediaOnly = channels.filter(
    (c) => picked.has(c.id) && ['instagram', 'youtube', 'tiktok'].includes(c.platform.toLowerCase())
  );
  const needsMedia = mediaOnly.length > 0 && !image.trim();
  const laterOk = when !== 'later' || new Date(at).getTime() > Date.now();
  const canPost = picked.size > 0 && text.trim().length > 0 && !needsMedia && laterOk && !busy;

  const post = async () => {
    if (!accountId || !canPost) return;
    setBusy(true);
    const postType = image.trim() ? 'photo' : link.trim() ? 'link' : 'text';
    const scheduleTime =
      when === 'now' ? 'now' : when === 'later' ? String(new Date(at).getTime()) : null;
    let done = 0;
    try {
      for (const id of picked) {
        await createPost({
          variables: {
            input: {
              accountId,
              socialProfileId: id,
              title: text.trim(),
              summary: text.trim(),
              postUrl: link.trim() || null,
              imageUrl: image.trim() || null,
              postType,
              scheduleTime,
              isDraft: when === 'draft',
            },
          },
        });
        done += 1;
      }
      showInkToast(
        when === 'draft'
          ? `Saved as ${done === 1 ? 'a draft' : `${done} drafts`}.`
          : when === 'now'
            ? `Posting to ${done} ${done === 1 ? 'channel' : 'channels'} in a minute.`
            : `Scheduled on ${done} ${done === 1 ? 'channel' : 'channels'}.`
      );
      onPosted();
    } catch (err) {
      showInkToast(
        (err instanceof Error ? err.message : 'That didn’t work.') +
          (done ? ` (${done} already created.)` : '')
      );
      if (done) onPosted();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="ink-modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="ink-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wp-title"
        style={{ width: 'min(560px, 100%)' }}
      >
        <div className="ink-modal-title" id="wp-title">
          Write a post
        </div>
        <div className="aoe-form">
          <div className="ink-site-label">
            Channels
            {channels.length ? (
              <div className="chp-list">
                {channels.map((c) => (
                  <label className="chp-row" key={c.id}>
                    <input
                      type="checkbox"
                      checked={picked.has(c.id)}
                      onChange={() => toggle(c.id)}
                    />
                    <span className="rc-mark rc-mark-sm">{channelMark(c.platform)}</span>
                    <span className="chp-name">
                      <span>{c.profileName || c.profileUsername || channelName(c.platform)}</span>
                      <span className="ink-muted">{channelName(c.platform)}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <span className="ink-muted">No channels connected yet.</span>
            )}
          </div>
          <label className="ink-site-label">
            Text
            <textarea
              className="ink-site-input"
              rows={4}
              value={text}
              autoFocus
              maxLength={5000}
              placeholder="What do you want to say?"
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <div className="aoe-two">
            <label className="ink-site-label">
              Link <span className="ink-muted">(optional)</span>
              <input
                className="ink-site-input"
                type="url"
                value={link}
                placeholder="https://"
                onChange={(e) => setLink(e.target.value)}
              />
            </label>
            <label className="ink-site-label">
              Image address <span className="ink-muted">(optional)</span>
              <input
                className="ink-site-input"
                type="url"
                value={image}
                placeholder="https://…/photo.jpg"
                onChange={(e) => setImage(e.target.value)}
              />
            </label>
          </div>
          {needsMedia ? (
            <span className="ink-muted aoe-help" style={{ color: 'var(--ink-amber)' }}>
              {mediaOnly.map((c) => channelName(c.platform)).join(', ')} posts need an image.
            </span>
          ) : null}
          <div className="ink-site-label">
            When
            <span className="ink-seg" style={{ alignSelf: 'flex-start' }}>
              {(
                [
                  ['now', 'Now'],
                  ['later', 'Pick a time'],
                  ['draft', 'Save as draft'],
                ] as Array<[When, string]>
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  data-active={when === key}
                  onClick={() => setWhen(key)}
                >
                  {label}
                </button>
              ))}
            </span>
            {when === 'later' ? (
              <input
                className="ink-site-input"
                type="datetime-local"
                value={at}
                min={localInputValue(new Date())}
                onChange={(e) => setAt(e.target.value)}
                aria-invalid={!laterOk}
              />
            ) : null}
          </div>
        </div>
        <div className="ink-modal-actions">
          <button type="button" className="ink-btn ink-btn-quiet" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            disabled={!canPost}
            onClick={() => void post()}
          >
            {busy
              ? 'Posting…'
              : when === 'draft'
                ? 'Save draft'
                : when === 'now'
                  ? 'Post now'
                  : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}
